/**
 * Availability / pre-order split helpers.
 *
 * The Xhosa B2B API returns, per SKU, an `availableQuantity`: the number of units that can ship
 * immediately (effective B2B stock minus active reservations and pending orders, never negative).
 * When a partner orders more than that, the backend automatically ships the available units now as a
 * normal order and back-orders the remainder as a pre-order. These helpers compute the matching UI
 * numbers so the message shown to the partner reflects exactly what the backend does.
 *
 * `availableQuantity` is intentionally nullable: `null`/`undefined` means "unknown" (the delivery
 * estimate was not fetched or the API did not provide it), in which case no availability hint is shown.
 */

export interface AvailabilitySplit {
  /** Units that ship immediately as a normal order. */
  available: number
  /** Units back-ordered as a pre-order (chosen − available, never negative). */
  rest: number
  /** True when the chosen quantity exceeds what can ship immediately. */
  isSplit: boolean
}

/**
 * Whether we have a usable, finite available quantity to display.
 * Excludes null/undefined (unknown) and negative values (defensive).
 */
export function hasKnownAvailableQuantity(availableQuantity?: number | null): boolean {
  return typeof availableQuantity === 'number' && Number.isFinite(availableQuantity) && availableQuantity >= 0
}

/**
 * Split a chosen line quantity into the part that ships immediately and the part that becomes a
 * pre-order, given how many units are available right now.
 *
 * When the available quantity is unknown, nothing is split (everything counts as "available" and
 * `isSplit` is false) so callers fall back to the existing delivery/pre-order wording.
 */
export function splitQuantityByAvailability(chosen: number, availableQuantity?: number | null): AvailabilitySplit {
  const safeChosen = Math.max(0, Math.floor(chosen || 0))

  if (!hasKnownAvailableQuantity(availableQuantity)) {
    return { available: safeChosen, rest: 0, isSplit: false }
  }

  const available = Math.min(safeChosen, availableQuantity as number)
  const rest = Math.max(0, safeChosen - available)

  return { available, rest, isSplit: rest > 0 }
}
